import { format } from "date-fns";
import firebaseApp from "services/firebase";
import i18n, { dateLocale } from "i18n";
import {
  dischargeInstructionTitle,
  sortDischargeInstructions,
} from "utils/dischargeInstructions";

const functions = firebaseApp.functions();

const exportClinicalNotePdfCallable = functions.httpsCallable(
  "exportClinicalNotePdf",
  { timeout: 60000 },
);

/**
 * Export a clinical note to PDF via Cloud Function.
 * @param {object} params
 * @param {string} params.templateName
 * @param {Array<{ key: string, title: string }>} params.sections
 * @param {Record<string, string>} params.noteSections
 * @param {string} [params.generatedAt] ISO timestamp
 * @returns {Promise<{ fileName: string, blob: Blob }>}
 */
export async function exportClinicalNotePdf({
  templateName,
  sections,
  noteSections,
  generatedAt,
  subtitle,
}) {
  const payload = {
    templateName,
    sections,
    noteSections,
  };
  // Omitted keeps the note's "Clinical Documentation" line; "" drops it.
  if (typeof subtitle === "string") payload.subtitle = subtitle;

  if (generatedAt) {
    payload.generatedAt = generatedAt;
  }

  const result = await exportClinicalNotePdfCallable(payload);
  const { pdfBase64, fileName, mimeType } = result?.data || {};

  if (!pdfBase64) {
    throw new Error("PDF export did not return file data.");
  }

  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  const blob = new Blob([bytes], {
    type: mimeType || "application/pdf",
  });

  return {
    fileName: fileName || "clinical-note.pdf",
    blob,
  };
}

/**
 * Trigger a browser download for a Blob.
 * @param {Blob} blob
 * @param {string} fileName
 */
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

// exportClinicalNotePdf's own cap on sections. Above it, instructions are merged by
// category so a long set still exports rather than failing.
const MAX_PDF_SECTIONS = 32;

// Category ids whose PDF section heading is `medications:discharge.pdfSections.<id>`,
// translated when the PDF is built. Anything else falls under "other".
const DISCHARGE_CATEGORIES = [
  "medication_general",
  "medication_specific",
  "activity",
  "diet",
  "followup",
  "warning_signs",
  "wound_care",
  "other",
];

const dischargeCategoryLabel = (category) =>
  i18n.t(
    `medications:discharge.pdfSections.${
      DISCHARGE_CATEGORIES.includes(category) ? category : "other"
    }`,
  );

/**
 * Download a set of patient discharge instructions as a PDF.
 *
 * Reuses the clinical note's generator, which takes any titled sections, so the
 * provider's Discharge step, the Chart step and the patient's phone all produce the
 * same kind of file from the same code. Works for an anonymous caller too: the
 * callable only needs a signed-in user, and the QR page always has one.
 *
 * @param {Array<{ title: string, category?: string, prose: string, medicationName?: string }>} instructions
 */
export async function downloadDischargeInstructionsPdf(instructions) {
  const ordered = sortDischargeInstructions(instructions).filter((inst) => inst?.prose);
  if (ordered.length === 0) throw new Error(i18n.t("medications:discharge.pdfEmpty"));

  let sections;
  let noteSections;
  if (ordered.length <= MAX_PDF_SECTIONS) {
    sections = ordered.map((inst, idx) => ({
      key: `i${idx}`,
      title: dischargeInstructionTitle(inst),
    }));
    noteSections = Object.fromEntries(ordered.map((inst, idx) => [`i${idx}`, inst.prose]));
  } else {
    const groups = {};
    ordered.forEach((inst) => {
      const key = inst.category || "other";
      groups[key] = [...(groups[key] || []), `${dischargeInstructionTitle(inst)}\n${inst.prose}`];
    });
    sections = Object.keys(groups).map((key) => ({
      key,
      title: dischargeCategoryLabel(key),
    }));
    noteSections = Object.fromEntries(
      Object.entries(groups).map(([key, parts]) => [key, parts.join("\n\n")]),
    );
  }

  const { blob } = await exportClinicalNotePdf({
    templateName: i18n.t("medications:discharge.title"),
    subtitle: format(new Date(), i18n.t("medications:discharge.dateFormat"), {
      locale: dateLocale(),
    }),
    sections,
    noteSections,
  });
  downloadBlob(blob, `discharge_instructions_${format(new Date(), "yyyy_MM_dd")}.pdf`);
}
